// Exceeds target ceiling — single responsibility justified (authentication state and session management)
import { createContext, useContext, useEffect, useState, useCallback, useRef, type ReactNode } from 'react'
import type { User, Session } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import type { ProfileSubscriptionStatus } from '../types/user'

interface AuthState {
  user: User | null
  session: Session | null
  loading: boolean
  subscriptionStatus: ProfileSubscriptionStatus | null
  isReadOnly: boolean
  trialEndsAt: string | null
  signInWithMagicLink: (email: string, redirectTo?: string) => Promise<void>
  signInWithPassword: (email: string, password: string) => Promise<void>
  signUpWithPassword: (email: string, password: string) => Promise<void>
  signInWithGoogle: (redirectTo?: string) => Promise<void>
  resetPassword: (email: string) => Promise<void>
  signOut: () => Promise<void>
  refreshUser: () => Promise<void>
  refreshSubscription: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

// Debounce interval for subscription refresh on route change (ms)
const SUBSCRIPTION_REFRESH_INTERVAL = 30_000

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [subscriptionStatus, setSubscriptionStatus] = useState<ProfileSubscriptionStatus | null>(null)
  const [isReadOnly, setIsReadOnly] = useState(false)
  const [trialEndsAt, setTrialEndsAt] = useState<string | null>(null)
  const lastSubscriptionFetch = useRef<number>(0)

  // Fetch subscription context from the RPC for landlord/agent users
  const fetchSubscriptionContext = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase.rpc('get_current_landlord_context')

      if (error) {
        // RPC may not exist yet (pre-migration) or user is not landlord/agent
        // Fall back gracefully — non-landlord roles get no subscription gating
        return
      }

      const result = data?.[0]
      if (result) {
        setSubscriptionStatus(result.subscription_status as ProfileSubscriptionStatus)
        setIsReadOnly(result.is_read_only)
        setTrialEndsAt(result.trial_ends_at)
        lastSubscriptionFetch.current = Date.now()
      }
    } catch {
      // Silently fail — subscription context is non-critical for page load
    }
  }, [])

  // Refresh subscription status (debounced — max once per 30s)
  const refreshSubscription = useCallback(async () => {
    if (!user) return
    const now = Date.now()
    if (now - lastSubscriptionFetch.current < SUBSCRIPTION_REFRESH_INTERVAL) return
    await fetchSubscriptionContext(user.id)
  }, [user, fetchSubscriptionContext])

  useEffect(() => {
    // Initial session check
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)

      // Fetch subscription context for authenticated users
      if (session?.user) {
        await fetchSubscriptionContext(session.user.id)
      }

      setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Redirect immediately on sign out
      if (event === 'SIGNED_OUT') {
        setSession(null)
        setUser(null)
        setSubscriptionStatus(null)
        setIsReadOnly(false)
        setTrialEndsAt(null)
        setLoading(false)
        window.location.replace('/login')
        return
      }

      setSession(session)
      // Only update user if the user ID changed to prevent unnecessary re-renders
      setUser(prev => {
        const newUser = session?.user ?? null
        if (prev?.id === newUser?.id) return prev
        return newUser
      })

      // Refresh subscription context when user signs in
      if (event === 'SIGNED_IN' && session?.user) {
        fetchSubscriptionContext(session.user.id)
      }

      setLoading(false)
    })

    return () => subscription.unsubscribe()
  }, [fetchSubscriptionContext])

  const signInWithMagicLink = async (email: string, redirectTo?: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: redirectTo ?? `${window.location.origin}/auth/callback`,
      },
    })
    if (error) throw error
  }

  const signInWithPassword = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) {
      if (error.message?.includes('Invalid login credentials')) {
        throw new Error('Invalid email or password')
      }
      throw error
    }
  }

  const signUpWithPassword = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    })

    if (error) {
      if (error.message?.includes('already registered')) {
        throw new Error('An account with this email already exists. Please sign in instead.')
      }
      throw error
    }

    // If no session returned, email confirmation is required
    if (!data.session) {
      throw new Error('Please check your email to confirm your account before signing in.')
    }
  }

  const signInWithGoogle = async (redirectTo?: string) => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectTo ?? `${window.location.origin}/auth/callback`,
      },
    })
    if (error) throw error
  }

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`,
    })
    if (error) throw error
  }

  const signOut = async () => {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  }

  const refreshUser = async () => {
    // Refresh session to get updated user data including new identities
    const { data: { session: newSession } } = await supabase.auth.refreshSession()
    if (newSession) {
      setSession(newSession)
      setUser(newSession.user)
    } else {
      // Fallback to getUser if session refresh fails
      const { data } = await supabase.auth.getUser()
      if (data.user) {
        setUser(data.user)
      }
    }
  }

  return (
    <AuthContext.Provider value={{
      user,
      session,
      loading,
      subscriptionStatus,
      isReadOnly,
      trialEndsAt,
      signInWithMagicLink,
      signInWithPassword,
      signUpWithPassword,
      signInWithGoogle,
      resetPassword,
      signOut,
      refreshUser,
      refreshSubscription,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
